import React, {
    Fragment,
    useEffect,
    useMemo,
    useState
} from "react";

import { Link } from "react-router-dom";
import axios from "axios";
import { GlobalSet } from "../../global/GlobalProvider";
import { useTranslation } from "react-i18next";


/* =========================================================
   CONFIG
========================================================= */

const API_KEY =
    "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9";

const PER_PAGE = 10;


/* =========================================================
   TEAM LEVELS
========================================================= */

const TEAM_LEVELS = [
    { id: 1, label: "Level 1", slug: "level-a" },
    { id: 2, label: "Level 2", slug: "level-b" },
    { id: 3, label: "Level 3", slug: "level-c" },
    { id: 4, label: "Level 4", slug: "level-d" },
    { id: 5, label: "Level 5", slug: "level-e" },
    { id: 6, label: "Level 6", slug: "level-f" },
    { id: 7, label: "Level 7", slug: "level-g" },
    { id: 8, label: "Level 8", slug: "level-h" },
    { id: 9, label: "Level 9", slug: "level-i" }
];


/* =========================================================
   HELPERS
========================================================= */

const numberFormat = (value) => {

    const number = Number(value || 0);

    if (Number.isNaN(number)) {
        return "0";
    }

    return new Intl.NumberFormat("en-US", {
        maximumFractionDigits: 2
    }).format(number);
};


const formatDate = (value) => {

    if (!value) {
        return "—";
    }

    try {

        const normalized =
            String(value).replace(" ", "T");

        const date =
            new Date(normalized);

        if (Number.isNaN(date.getTime())) {
            return value;
        }

        return date.toLocaleDateString(
            "en-GB",
            {
                day: "2-digit",
                month: "short",
                year: "numeric"
            }
        );

    } catch (error) {

        return value;
    }
};


const getInitial = (value) => {

    if (!value) {
        return "U";
    }

    return String(value)
        .trim()
        .charAt(0)
        .toUpperCase();
};


/* =========================================================
   MAIN COMPONENT
========================================================= */

const BasicDatatable = () => {

    const { t } =
        useTranslation();

    const { CurrencySymbol } =
        GlobalSet();


    /* =====================================================
       STATES
    ===================================================== */

    const [income, setIncome] =
        useState([]);

    const [loading, setLoading] =
        useState(true);

    const [pageError, setPageError] =
        useState("");

    const [search, setSearch] =
        useState("");

    const [currentPage, setCurrentPage] =
        useState(1);


    /* =====================================================
       CURRENCY
    ===================================================== */

    const currencySymbol =
        typeof CurrencySymbol === "string"
            ? CurrencySymbol
            : "৳";


    /* =====================================================
       FETCH SMART TEAM
    ===================================================== */

    const fetchSmartTeam =
        async () => {

            const authToken =
                localStorage.getItem(
                    "auth_token"
                );

            const selectedCurrency =
                localStorage.getItem(
                    "selectedCurrency"
                );


            if (!authToken) {

                setIncome([]);

                setLoading(false);

                setPageError(
                    "Your login session was not found. Please login again."
                );

                return;
            }


            const data = {

                key_s: API_KEY,

                currency:
                    selectedCurrency

            };


            try {

                setLoading(true);

                setPageError("");


                const response =
                    await axios.post(
                        "/smart-team",
                        data
                    );


                const rows =
                    response?.data?.data;


                setIncome(
                    Array.isArray(rows)
                        ? rows
                        : []
                );


            } catch (error) {

                console.error(
                    "Smart Team Error:",
                    error
                );


                setIncome([]);


                setPageError(
                    error?.response?.data?.message ||
                    "Unable to load your smart team. Please try again."
                );


            } finally {

                setLoading(false);
            }
        };


    /* =====================================================
       LOAD DATA
    ===================================================== */

    useEffect(() => {

        fetchSmartTeam();

        // eslint-disable-next-line
    }, []);


    /* =====================================================
       SEARCH / FILTER
    ===================================================== */

    const filteredIncome =
        useMemo(() => {

            const keyword =
                search
                    .trim()
                    .toLowerCase();


            if (!keyword) {
                return income;
            }


            return income.filter(
                row => {

                    const searchable = [

                        row.id,
                        row.email,
                        row.count_active_spons,
                        row.total_invest,
                        row.team_and_spon_sell,
                        row.team_sell,
                        row.created_at

                    ]
                        .filter(
                            value =>
                                value !== null &&
                                value !== undefined
                        )
                        .join(" ")
                        .toLowerCase();


                    return searchable.includes(
                        keyword
                    );
                }
            );

        }, [
            income,
            search
        ]);


    useEffect(() => {

        setCurrentPage(1);

    }, [search]);


    /* =====================================================
       SUMMARY
    ===================================================== */

    const summary =
        useMemo(() => {

            return income.reduce(
                (result, row) => {

                    result.totalMembers += 1;

                    result.activeSponsors +=
                        Number(
                            row.count_active_spons ||
                            0
                        );

                    result.selfSales +=
                        Number(
                            row.total_invest ||
                            0
                        );

                    result.selfAndTeam +=
                        Number(
                            row.team_and_spon_sell ||
                            0
                        );

                    result.teamSales +=
                        Number(
                            row.team_sell ||
                            0
                        );


                    return result;
                },
                {
                    totalMembers: 0,
                    activeSponsors: 0,
                    selfSales: 0,
                    selfAndTeam: 0,
                    teamSales: 0
                }
            );

        }, [income]);


    /* =====================================================
       PAGINATION
    ===================================================== */

    const totalPages =
        Math.max(
            1,
            Math.ceil(
                filteredIncome.length /
                PER_PAGE
            )
        );


    useEffect(() => {

        if (currentPage > totalPages) {

            setCurrentPage(
                totalPages
            );
        }

    }, [
        currentPage,
        totalPages
    ]);


    const paginatedIncome =
        useMemo(() => {

            const start =
                (currentPage - 1) *
                PER_PAGE;


            return filteredIncome.slice(
                start,
                start + PER_PAGE
            );

        }, [
            filteredIncome,
            currentPage
        ]);


    const pageNumbers =
        useMemo(() => {

            let start =
                Math.max(
                    1,
                    currentPage - 2
                );

            let end =
                Math.min(
                    totalPages,
                    currentPage + 2
                );


            if (
                end - start < 4
            ) {

                if (start === 1) {

                    end =
                        Math.min(
                            5,
                            totalPages
                        );

                } else {

                    start =
                        Math.max(
                            1,
                            totalPages - 4
                        );
                }
            }


            const pages = [];


            for (
                let i = start;
                i <= end;
                i++
            ) {

                pages.push(i);
            }


            return pages;

        }, [
            currentPage,
            totalPages
        ]);


    const changePage =
        page => {

            if (
                page < 1 ||
                page > totalPages
            ) {
                return;
            }

            setCurrentPage(page);
        };


    const showingFrom =
        filteredIncome.length === 0
            ? 0
            : (
                (currentPage - 1) *
                PER_PAGE
            ) + 1;


    const showingTo =
        Math.min(
            currentPage *
            PER_PAGE,
            filteredIncome.length
        );


    /* =====================================================
       RENDER
    ===================================================== */

    return (

        <Fragment>


            {/* =================================================
                STYLE
            ================================================= */}

            <style>{`

                * {
                    box-sizing: border-box;
                }


                .smart-team-page {

                    --primary: #635bff;
                    --primary-dark: #5147ee;
                    --primary-soft: #f2f1ff;

                    --green: #12b76a;
                    --green-dark: #067647;
                    --green-soft: #ecfdf3;

                    --blue: #1570ef;
                    --blue-dark: #175cd3;
                    --blue-soft: #eff8ff;

                    --orange: #f79009;
                    --orange-dark: #b54708;
                    --orange-soft: #fff7ed;

                    --purple: #7f56d9;
                    --purple-soft: #f4f3ff;

                    --red: #f04438;
                    --red-soft: #fef3f2;

                    --text: #101828;
                    --text2: #344054;
                    --muted: #667085;

                    --border: #e4e7ec;
                    --light: #f9fafb;

                    width: 100%;

                    padding: 20px;

                    border-radius: 20px;

                    background: #ffffff;

                    color: var(--text);

                    font-family:
                        Inter,
                        -apple-system,
                        BlinkMacSystemFont,
                        "Segoe UI",
                        Arial,
                        sans-serif;
                }


                .smart-team-container {

                    width: 100%;

                    max-width: 1500px;

                    margin: 0 auto;
                }


                /* =============================================
                   HEADER
                ============================================= */

                .smart-team-header {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 20px;

                    margin-bottom: 20px;
                }


                .smart-team-header-left {

                    display: flex;

                    align-items: center;

                    gap: 14px;
                }


                .smart-team-header-icon {

                    width: 54px;
                    height: 54px;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    flex-shrink: 0;

                    border-radius: 16px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);

                    font-size: 21px;
                }


                .smart-team-header h2 {

                    margin:
                        0 0 4px;

                    color:
                        var(--text);

                    font-size: 27px;

                    line-height: 1.2;

                    font-weight: 800;
                }


                .smart-team-header p {

                    margin: 0;

                    color:
                        var(--muted);

                    font-size: 12px;

                    line-height: 1.5;
                }


                .smart-team-header-badge {

                    display: inline-flex;

                    align-items: center;

                    gap: 7px;

                    min-height: 39px;

                    padding:
                        0 14px;

                    border:
                        1px solid
                        #d9d6fe;

                    border-radius: 10px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);

                    font-size: 11px;

                    font-weight: 800;
                }


                /* =============================================
                   LEVEL NAVIGATION
                ============================================= */

                .team-level-card {

                    width: 100%;

                    margin-bottom: 18px;

                    padding: 7px;

                    overflow-x: auto;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 14px;

                    background:
                        #ffffff;

                    box-shadow:
                        0 4px 16px
                        rgba(
                            16,
                            24,
                            40,
                            .035
                        );

                    scrollbar-width: thin;
                }


                .team-level-card::-webkit-scrollbar {

                    height: 5px;
                }


                .team-level-card::-webkit-scrollbar-thumb {

                    border-radius: 20px;

                    background: #d0d5dd;
                }


                .team-level-list {

                    display: flex;

                    align-items: center;

                    gap: 6px;

                    min-width: max-content;
                }


                .team-level-link {

                    min-height: 41px;

                    display: inline-flex;

                    align-items: center;

                    justify-content: center;

                    gap: 7px;

                    padding:
                        0 14px;

                    border-radius: 9px;

                    color:
                        var(--text2) !important;

                    font-size: 11px;

                    font-weight: 700;

                    text-decoration:
                        none !important;

                    transition:
                        .15s ease;
                }


                .team-level-link:hover {

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary) !important;
                }


                .team-level-link.all-active {

                    background:
                        var(--primary);

                    color:
                        #ffffff !important;

                    box-shadow:
                        0 4px 12px
                        rgba(
                            99,
                            91,
                            255,
                            .18
                        );
                }


                .team-level-separator {

                    width: 1px;
                    height: 27px;

                    margin:
                        0 2px;

                    background:
                        var(--border);
                }


                /* =============================================
                   SUMMARY
                ============================================= */

                .smart-summary-grid {

                    display: grid;

                    grid-template-columns:
                        repeat(
                            4,
                            minmax(0, 1fr)
                        );

                    gap: 13px;

                    margin-bottom: 18px;
                }


                .smart-summary-card {

                    position: relative;

                    overflow: hidden;

                    padding: 17px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 15px;

                    background:
                        #ffffff;

                    box-shadow:
                        0 5px 20px
                        rgba(
                            16,
                            24,
                            40,
                            .04
                        );
                }


                .smart-summary-header {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 10px;

                    margin-bottom: 12px;
                }


                .smart-summary-label {

                    color:
                        var(--muted);

                    font-size: 11px;

                    font-weight: 700;
                }


                .smart-summary-icon {

                    width: 40px;
                    height: 40px;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    border-radius: 11px;

                    font-size: 15px;
                }


                .smart-summary-icon.member {

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);
                }


                .smart-summary-icon.sponsor {

                    background:
                        var(--blue-soft);

                    color:
                        var(--blue);
                }


                .smart-summary-icon.self {

                    background:
                        var(--green-soft);

                    color:
                        var(--green-dark);
                }


                .smart-summary-icon.team {

                    background:
                        var(--orange-soft);

                    color:
                        var(--orange-dark);
                }


                .smart-summary-value {

                    margin:
                        0 0 4px;

                    color:
                        var(--text);

                    font-size: 22px;

                    line-height: 1.2;

                    font-weight: 800;

                    word-break: break-word;
                }


                .smart-summary-description {

                    margin: 0;

                    color:
                        #98a2b3;

                    font-size: 9px;
                }


                /* =============================================
                   MAIN TABLE CARD
                ============================================= */

                .smart-team-main-card {

                    overflow: hidden;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 17px;

                    background:
                        #ffffff;

                    box-shadow:
                        0 6px 25px
                        rgba(
                            16,
                            24,
                            40,
                            .045
                        );
                }


                .smart-team-card-header {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 20px;

                    padding:
                        18px 20px;

                    border-bottom:
                        1px solid
                        #eef0f4;
                }


                .smart-team-card-title h3 {

                    margin:
                        0 0 4px;

                    color:
                        var(--text);

                    font-size: 17px;

                    font-weight: 800;
                }


                .smart-team-card-title p {

                    margin: 0;

                    color:
                        var(--muted);

                    font-size: 11px;
                }


                /* =============================================
                   SEARCH
                ============================================= */

                .smart-team-search {

                    position: relative;

                    width: 310px;

                    max-width: 100%;
                }


                .smart-team-search i {

                    position: absolute;

                    top: 50%;
                    left: 14px;

                    transform:
                        translateY(-50%);

                    color:
                        #98a2b3;

                    font-size: 12px;

                    pointer-events: none;
                }


                .smart-team-search input {

                    width: 100%;
                    height: 43px;

                    padding:
                        0 14px
                        0 40px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 10px;

                    outline: none;

                    background:
                        var(--light);

                    color:
                        var(--text);

                    font-size: 12px;

                    transition:
                        .18s ease;
                }


                .smart-team-search input::placeholder {

                    color:
                        #98a2b3;
                }


                .smart-team-search input:focus {

                    border-color:
                        var(--primary);

                    background:
                        #ffffff;

                    box-shadow:
                        0 0 0 4px
                        rgba(
                            99,
                            91,
                            255,
                            .08
                        );
                }


                /* =============================================
                   TABLE
                ============================================= */

                .smart-team-table-wrap {

                    width: 100%;

                    overflow-x: auto;
                }


                .smart-team-table {

                    width: 100%;

                    min-width: 1100px;

                    border-collapse:
                        collapse;
                }


                .smart-team-table thead {

                    background:
                        #f9fafb;
                }


                .smart-team-table th {

                    padding:
                        14px 16px;

                    border-bottom:
                        1px solid
                        var(--border);

                    color:
                        var(--muted);

                    font-size: 10px;

                    font-weight: 800;

                    text-align: left;

                    text-transform:
                        uppercase;

                    letter-spacing:
                        .35px;

                    white-space: nowrap;
                }


                .smart-team-table td {

                    padding:
                        15px 16px;

                    border-bottom:
                        1px solid
                        #f0f1f4;

                    color:
                        var(--text2);

                    font-size: 12px;

                    vertical-align: middle;
                }


                .smart-team-table tbody tr {

                    transition:
                        .15s ease;
                }


                .smart-team-table tbody tr:hover {

                    background:
                        #fafaff;
                }


                .smart-team-table tbody tr:last-child td {

                    border-bottom: 0;
                }


                /* =============================================
                   SL
                ============================================= */

                .smart-team-sl {

                    width: 31px;
                    height: 31px;

                    display: inline-flex;

                    align-items: center;

                    justify-content: center;

                    border-radius: 9px;

                    background:
                        #f2f4f7;

                    color:
                        var(--text2);

                    font-size: 10px;

                    font-weight: 800;
                }


                /* =============================================
                   MEMBER
                ============================================= */

                .smart-member-box {

                    display: flex;

                    align-items: center;

                    gap: 10px;

                    min-width: 220px;
                }


                .smart-member-avatar {

                    width: 39px;
                    height: 39px;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    flex-shrink: 0;

                    border-radius: 11px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);

                    font-size: 13px;

                    font-weight: 800;
                }


                .smart-member-content {

                    min-width: 0;
                }


                .smart-member-content strong {

                    display: block;

                    max-width: 240px;

                    overflow: hidden;

                    color:
                        var(--text);

                    font-size: 12px;

                    font-weight: 700;

                    text-overflow:
                        ellipsis;

                    white-space: nowrap;
                }


                .smart-member-content small {

                    display: block;

                    margin-top: 3px;

                    color:
                        #98a2b3;

                    font-size: 9px;
                }


                /* =============================================
                   MEMBER COUNT
                ============================================= */

                .smart-member-count {

                    display: inline-flex;

                    align-items: center;

                    gap: 6px;

                    padding:
                        6px 9px;

                    border-radius: 8px;

                    background:
                        var(--blue-soft);

                    color:
                        var(--blue-dark);

                    font-size: 10px;

                    font-weight: 800;

                    white-space: nowrap;
                }


                /* =============================================
                   AMOUNTS
                ============================================= */

                .smart-amount {

                    font-size: 12px;

                    font-weight: 800;

                    white-space: nowrap;
                }


                .smart-amount.self {

                    color:
                        var(--green-dark);
                }


                .smart-amount.network {

                    color:
                        var(--purple);
                }


                .smart-amount.team {

                    color:
                        var(--orange-dark);
                }


                /* =============================================
                   DATE
                ============================================= */

                .smart-date {

                    display: inline-flex;

                    align-items: center;

                    gap: 7px;

                    color:
                        var(--muted);

                    font-size: 10px;

                    white-space: nowrap;
                }


                .smart-date-icon {

                    width: 30px;
                    height: 30px;

                    display: inline-flex;

                    align-items: center;

                    justify-content: center;

                    border-radius: 8px;

                    background:
                        #f2f4f7;

                    color:
                        var(--muted);
                }


                /* =============================================
                   LOADING / ERROR / EMPTY
                ============================================= */

                .smart-team-state {

                    padding:
                        70px 20px;

                    text-align: center;
                }


                .smart-team-spinner {

                    width: 40px;
                    height: 40px;

                    margin:
                        0 auto 15px;

                    border:
                        4px solid
                        #eaecf0;

                    border-top-color:
                        var(--primary);

                    border-radius: 50%;

                    animation:
                        smartTeamSpin
                        .75s linear
                        infinite;
                }


                @keyframes smartTeamSpin {

                    to {

                        transform:
                            rotate(360deg);
                    }
                }


                .smart-state-icon {

                    width: 60px;
                    height: 60px;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    margin:
                        0 auto 14px;

                    border-radius: 17px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);

                    font-size: 22px;
                }


                .smart-team-state h4 {

                    margin:
                        0 0 5px;

                    color:
                        var(--text);

                    font-size: 15px;

                    font-weight: 800;
                }


                .smart-team-state p {

                    max-width: 430px;

                    margin:
                        0 auto;

                    color:
                        var(--muted);

                    font-size: 11px;

                    line-height: 1.6;
                }


                .smart-retry-button {

                    min-height: 40px;

                    margin-top: 15px;

                    padding:
                        0 17px;

                    border: 0;

                    border-radius: 9px;

                    background:
                        var(--primary);

                    color:
                        #ffffff;

                    font-size: 11px;

                    font-weight: 800;

                    cursor: pointer;
                }


                /* =============================================
                   PAGINATION
                ============================================= */

                .smart-team-footer {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 15px;

                    padding:
                        15px 20px;

                    border-top:
                        1px solid
                        #eef0f4;
                }


                .smart-team-info {

                    color:
                        var(--muted);

                    font-size: 11px;
                }


                .smart-team-info strong {

                    color:
                        var(--text2);
                }


                .smart-pagination {

                    display: flex;

                    align-items: center;

                    gap: 5px;
                }


                .smart-page-btn {

                    min-width: 36px;
                    height: 36px;

                    display: inline-flex;

                    align-items: center;

                    justify-content: center;

                    padding:
                        0 9px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 8px;

                    outline: none;

                    background:
                        #ffffff;

                    color:
                        var(--text2);

                    font-size: 11px;

                    font-weight: 700;

                    cursor: pointer;

                    transition:
                        .15s ease;
                }


                .smart-page-btn:hover:not(:disabled) {

                    border-color:
                        var(--primary);

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);
                }


                .smart-page-btn.active {

                    border-color:
                        var(--primary);

                    background:
                        var(--primary);

                    color:
                        #ffffff;
                }


                .smart-page-btn:disabled {

                    opacity: .4;

                    cursor: not-allowed;
                }


                /* =============================================
                   MOBILE CARDS
                ============================================= */

                .smart-team-mobile {

                    display: none;
                }


                .smart-mobile-list {

                    padding: 12px;

                    background:
                        #f9fafb;
                }


                .smart-mobile-card {

                    padding: 15px;

                    margin-bottom: 11px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 13px;

                    background:
                        #ffffff;
                }


                .smart-mobile-card:last-child {

                    margin-bottom: 0;
                }


                .smart-mobile-header {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 10px;

                    padding-bottom: 12px;

                    margin-bottom: 12px;

                    border-bottom:
                        1px solid
                        #f0f1f4;
                }


                .smart-mobile-grid {

                    display: grid;

                    grid-template-columns:
                        repeat(
                            2,
                            minmax(0,1fr)
                        );

                    gap: 9px;
                }


                .smart-mobile-item {

                    padding: 10px;

                    border-radius: 9px;

                    background:
                        #f9fafb;
                }


                .smart-mobile-item span {

                    display: block;

                    margin-bottom: 4px;

                    color:
                        var(--muted);

                    font-size: 9px;

                    font-weight: 600;
                }


                .smart-mobile-item strong {

                    display: block;

                    color:
                        var(--text);

                    font-size: 11px;

                    font-weight: 800;

                    word-break: break-word;
                }


                .smart-mobile-join {

                    display: flex;

                    align-items: center;

                    gap: 7px;

                    padding-top: 12px;

                    margin-top: 12px;

                    border-top:
                        1px solid
                        #f0f1f4;

                    color:
                        var(--muted);

                    font-size: 10px;
                }


                /* =============================================
                   RESPONSIVE
                ============================================= */

                @media (
                    max-width: 1100px
                ) {

                    .smart-summary-grid {

                        grid-template-columns:
                            repeat(
                                2,
                                minmax(0,1fr)
                            );
                    }
                }


                @media (
                    max-width: 767px
                ) {

                    .smart-team-page {

                        padding: 13px;

                        border-radius: 14px;
                    }


                    .smart-team-header {

                        align-items:
                            flex-start;

                        flex-direction:
                            column;
                    }


                    .smart-team-header-left {

                        align-items:
                            flex-start;
                    }


                    .smart-team-header-icon {

                        width: 46px;
                        height: 46px;

                        border-radius: 12px;
                    }


                    .smart-team-header h2 {

                        font-size: 22px;
                    }


                    .smart-team-header-badge {

                        width: 100%;

                        justify-content:
                            center;
                    }


                    .smart-summary-grid {

                        grid-template-columns:
                            repeat(
                                2,
                                minmax(0,1fr)
                            );

                        gap: 9px;
                    }


                    .smart-summary-card {

                        padding: 14px;
                    }


                    .smart-summary-value {

                        font-size: 18px;
                    }


                    .smart-team-card-header {

                        align-items:
                            stretch;

                        flex-direction:
                            column;
                    }


                    .smart-team-search {

                        width: 100%;
                    }


                    .smart-team-desktop {

                        display: none;
                    }


                    .smart-team-mobile {

                        display: block;
                    }


                    .smart-team-footer {

                        align-items:
                            flex-start;

                        flex-direction:
                            column;
                    }


                    .smart-pagination {

                        width: 100%;

                        flex-wrap: wrap;

                        justify-content:
                            center;
                    }
                }


                @media (
                    max-width: 480px
                ) {

                    .smart-team-page {

                        padding: 9px;
                    }


                    .smart-summary-grid {

                        grid-template-columns:
                            1fr 1fr;
                    }


                    .smart-summary-label {

                        font-size: 9px;
                    }


                    .smart-summary-icon {

                        width: 34px;
                        height: 34px;

                        font-size: 13px;
                    }


                    .smart-summary-value {

                        font-size: 15px;
                    }


                    .smart-mobile-grid {

                        grid-template-columns:
                            1fr 1fr;
                    }
                }

            `}</style>


            {/* =================================================
                PAGE
            ================================================= */}

            <main className="smart-team-page">

                <div className="smart-team-container">


                    {/* =========================================
                        HEADER
                    ========================================= */}

                    <div className="smart-team-header">

                        <div className="smart-team-header-left">

                            <div className="smart-team-header-icon">

                                <i
                                    className="fa fa-sitemap"
                                    aria-hidden="true"
                                ></i>

                            </div>


                            <div>

                                <h2>
                                    My Smart Team
                                </h2>

                                <p>
                                    Monitor your network members,
                                    personal sales and team performance.
                                </p>

                            </div>

                        </div>


                        <div className="smart-team-header-badge">

                            <i
                                className="fa fa-users"
                                aria-hidden="true"
                            ></i>

                            {numberFormat(
                                summary.totalMembers
                            )} Team Members

                        </div>

                    </div>


                    {/* =========================================
                        LEVEL NAVIGATION
                    ========================================= */}

                    <div className="team-level-card">

                        <div className="team-level-list">


                            <Link
                                to="/my-smart-team"
                                className="team-level-link all-active"
                            >

                                <i
                                    className="fa fa-th-large"
                                    aria-hidden="true"
                                ></i>

                                All Levels

                            </Link>


                            <div className="team-level-separator"></div>


                            {TEAM_LEVELS.map(
                                level => (

                                    <Link
                                        key={level.id}
                                        to={
                                            `/view-team/${level.id}/${level.slug}`
                                        }
                                        className="team-level-link"
                                    >

                                        {level.label}

                                    </Link>

                                )
                            )}

                        </div>

                    </div>


                    {/* =========================================
                        SUMMARY
                    ========================================= */}

                    {!loading &&
                        !pageError && (

                        <div className="smart-summary-grid">


                            {/* TOTAL MEMBERS */}

                            <div className="smart-summary-card">

                                <div className="smart-summary-header">

                                    <span className="smart-summary-label">
                                        Team Members
                                    </span>

                                    <div className="smart-summary-icon member">

                                        <i
                                            className="fa fa-users"
                                            aria-hidden="true"
                                        ></i>

                                    </div>

                                </div>


                                <h3 className="smart-summary-value">

                                    {numberFormat(
                                        summary.totalMembers
                                    )}

                                </h3>


                                <p className="smart-summary-description">
                                    Total members shown in your network
                                </p>

                            </div>


                            {/* ACTIVE SPONSORS */}

                            <div className="smart-summary-card">

                                <div className="smart-summary-header">

                                    <span className="smart-summary-label">
                                        Active Sponsors
                                    </span>

                                    <div className="smart-summary-icon sponsor">

                                        <i
                                            className="fa fa-user-plus"
                                            aria-hidden="true"
                                        ></i>

                                    </div>

                                </div>


                                <h3 className="smart-summary-value">

                                    {numberFormat(
                                        summary.activeSponsors
                                    )}

                                </h3>


                                <p className="smart-summary-description">
                                    Total member connections
                                </p>

                            </div>


                            {/* SELF SALES */}

                            <div className="smart-summary-card">

                                <div className="smart-summary-header">

                                    <span className="smart-summary-label">
                                        Self Sales
                                    </span>

                                    <div className="smart-summary-icon self">

                                        <i
                                            className="fa fa-line-chart"
                                            aria-hidden="true"
                                        ></i>

                                    </div>

                                </div>


                                <h3 className="smart-summary-value">

                                    {currencySymbol}{" "}

                                    {numberFormat(
                                        summary.selfSales
                                    )}

                                </h3>


                                <p className="smart-summary-description">
                                    Combined personal sales
                                </p>

                            </div>


                            {/* TEAM SALES */}

                            <div className="smart-summary-card">

                                <div className="smart-summary-header">

                                    <span className="smart-summary-label">
                                        Team Sales
                                    </span>

                                    <div className="smart-summary-icon team">

                                        <i
                                            className="fa fa-bar-chart"
                                            aria-hidden="true"
                                        ></i>

                                    </div>

                                </div>


                                <h3 className="smart-summary-value">

                                    {currencySymbol}{" "}

                                    {numberFormat(
                                        summary.teamSales
                                    )}

                                </h3>


                                <p className="smart-summary-description">
                                    Combined network team sales
                                </p>

                            </div>

                        </div>

                    )}


                    {/* =========================================
                        MAIN CARD
                    ========================================= */}

                    <section className="smart-team-main-card">


                        {/* =====================================
                            CARD HEADER
                        ===================================== */}

                        <div className="smart-team-card-header">

                            <div className="smart-team-card-title">

                                <h3>
                                    All Team Members
                                </h3>

                                <p>
                                    View detailed sales and
                                    network performance.
                                </p>

                            </div>


                            {!loading &&
                                !pageError &&
                                income.length > 0 && (

                                <div className="smart-team-search">

                                    <i
                                        className="fa fa-search"
                                        aria-hidden="true"
                                    ></i>


                                    <input
                                        type="text"
                                        value={search}
                                        onChange={
                                            e =>
                                                setSearch(
                                                    e.target.value
                                                )
                                        }
                                        placeholder="Search by email, member..."
                                    />

                                </div>

                            )}

                        </div>


                        {/* =====================================
                            LOADING
                        ===================================== */}

                        {loading ? (

                            <div className="smart-team-state">

                                <div className="smart-team-spinner"></div>

                                <h4>
                                    Loading Smart Team
                                </h4>

                                <p>
                                    Please wait while we load
                                    your team information.
                                </p>

                            </div>

                        ) : pageError ? (


                            /* =================================
                               ERROR
                            ================================= */

                            <div className="smart-team-state">

                                <div className="smart-state-icon">

                                    <i
                                        className="fa fa-exclamation-triangle"
                                        aria-hidden="true"
                                    ></i>

                                </div>


                                <h4>
                                    Unable to Load Team
                                </h4>


                                <p>
                                    {pageError}
                                </p>


                                <button
                                    type="button"
                                    className="smart-retry-button"
                                    onClick={
                                        fetchSmartTeam
                                    }
                                >
                                    Try Again
                                </button>

                            </div>

                        ) : paginatedIncome.length === 0 ? (


                            /* =================================
                               EMPTY
                            ================================= */

                            <div className="smart-team-state">

                                <div className="smart-state-icon">

                                    <i
                                        className="fa fa-users"
                                        aria-hidden="true"
                                    ></i>

                                </div>


                                <h4>

                                    {search
                                        ? "No Matching Member Found"
                                        : "No Team Members Found"
                                    }

                                </h4>


                                <p>

                                    {search
                                        ? "We could not find any team member matching your search."
                                        : "Your smart team information will appear here when members are available."
                                    }

                                </p>

                            </div>

                        ) : (

                            <Fragment>


                                {/* =================================
                                    DESKTOP TABLE
                                ================================= */}

                                <div className="smart-team-desktop">

                                    <div className="smart-team-table-wrap">

                                        <table className="smart-team-table">

                                            <thead>

                                                <tr>

                                                    <th>
                                                        SL
                                                    </th>

                                                    <th>
                                                        {t("pro_his_bet_type") || "Member"}
                                                    </th>

                                                    <th>
                                                        {t("pro_his_odds_name") || "Members"}
                                                    </th>

                                                    <th>
                                                        Self Sell
                                                    </th>

                                                    <th>
                                                        Self And Team
                                                    </th>

                                                    <th>
                                                        Team Sells
                                                    </th>

                                                    <th>
                                                        Join Date
                                                    </th>

                                                </tr>

                                            </thead>


                                            <tbody>

                                                {paginatedIncome.map(
                                                    (
                                                        row,
                                                        index
                                                    ) => {

                                                        const serial =
                                                            (
                                                                currentPage -
                                                                1
                                                            ) *
                                                            PER_PAGE +
                                                            index +
                                                            1;


                                                        return (

                                                            <tr
                                                                key={
                                                                    row.id ||
                                                                    `${row.email}-${serial}`
                                                                }
                                                            >


                                                                {/* SL */}

                                                                <td>

                                                                    <span className="smart-team-sl">

                                                                        {serial}

                                                                    </span>

                                                                </td>


                                                                {/* MEMBER */}

                                                                <td>

                                                                    <div className="smart-member-box">

                                                                        <div className="smart-member-avatar">

                                                                            {getInitial(
                                                                                row.email
                                                                            )}

                                                                        </div>


                                                                        <div className="smart-member-content">

                                                                            <strong
                                                                                title={
                                                                                    row.email
                                                                                }
                                                                            >

                                                                                {row.email ||
                                                                                    "—"}

                                                                            </strong>

                                                                            <small>
                                                                                Team Member
                                                                            </small>

                                                                        </div>

                                                                    </div>

                                                                </td>


                                                                {/* MEMBER COUNT */}

                                                                <td>

                                                                    <span className="smart-member-count">

                                                                        <i
                                                                            className="fa fa-users"
                                                                            aria-hidden="true"
                                                                        ></i>

                                                                        {numberFormat(
                                                                            row.count_active_spons
                                                                        )}{" "}
                                                                        Members

                                                                    </span>

                                                                </td>


                                                                {/* SELF SELL */}

                                                                <td>

                                                                    <span className="smart-amount self">

                                                                        {currencySymbol}{" "}

                                                                        {numberFormat(
                                                                            row.total_invest
                                                                        )}

                                                                    </span>

                                                                </td>


                                                                {/* SELF + TEAM */}

                                                                <td>

                                                                    <span className="smart-amount network">

                                                                        {currencySymbol}{" "}

                                                                        {numberFormat(
                                                                            row.team_and_spon_sell
                                                                        )}

                                                                    </span>

                                                                </td>


                                                                {/* TEAM SELL */}

                                                                <td>

                                                                    <span className="smart-amount team">

                                                                        {currencySymbol}{" "}

                                                                        {numberFormat(
                                                                            row.team_sell
                                                                        )}

                                                                    </span>

                                                                </td>


                                                                {/* DATE */}

                                                                <td>

                                                                    <div className="smart-date">

                                                                        <span className="smart-date-icon">

                                                                            <i
                                                                                className="fa fa-calendar"
                                                                                aria-hidden="true"
                                                                            ></i>

                                                                        </span>


                                                                        {formatDate(
                                                                            row.created_at
                                                                        )}

                                                                    </div>

                                                                </td>

                                                            </tr>

                                                        );
                                                    }
                                                )}

                                            </tbody>

                                        </table>

                                    </div>

                                </div>


                                {/* =================================
                                    MOBILE VIEW
                                ================================= */}

                                <div className="smart-team-mobile">

                                    <div className="smart-mobile-list">

                                        {paginatedIncome.map(
                                            (
                                                row,
                                                index
                                            ) => {

                                                const serial =
                                                    (
                                                        currentPage -
                                                        1
                                                    ) *
                                                    PER_PAGE +
                                                    index +
                                                    1;


                                                return (

                                                    <div
                                                        className="smart-mobile-card"
                                                        key={
                                                            row.id ||
                                                            `${row.email}-${serial}`
                                                        }
                                                    >


                                                        {/* HEADER */}

                                                        <div className="smart-mobile-header">

                                                            <div className="smart-member-box">

                                                                <div className="smart-member-avatar">

                                                                    {getInitial(
                                                                        row.email
                                                                    )}

                                                                </div>


                                                                <div className="smart-member-content">

                                                                    <strong>

                                                                        {row.email ||
                                                                            "—"}

                                                                    </strong>

                                                                    <small>

                                                                        Member #{serial}

                                                                    </small>

                                                                </div>

                                                            </div>


                                                            <span className="smart-member-count">

                                                                {numberFormat(
                                                                    row.count_active_spons
                                                                )}

                                                                <i
                                                                    className="fa fa-users"
                                                                    aria-hidden="true"
                                                                ></i>

                                                            </span>

                                                        </div>


                                                        {/* DETAILS */}

                                                        <div className="smart-mobile-grid">


                                                            {/* SELF SELL */}

                                                            <div className="smart-mobile-item">

                                                                <span>
                                                                    Self Sell
                                                                </span>

                                                                <strong>

                                                                    {currencySymbol}{" "}

                                                                    {numberFormat(
                                                                        row.total_invest
                                                                    )}

                                                                </strong>

                                                            </div>


                                                            {/* TEAM SELL */}

                                                            <div className="smart-mobile-item">

                                                                <span>
                                                                    Team Sells
                                                                </span>

                                                                <strong>

                                                                    {currencySymbol}{" "}

                                                                    {numberFormat(
                                                                        row.team_sell
                                                                    )}

                                                                </strong>

                                                            </div>


                                                            {/* SELF + TEAM */}

                                                            <div className="smart-mobile-item">

                                                                <span>
                                                                    Self And Team
                                                                </span>

                                                                <strong>

                                                                    {currencySymbol}{" "}

                                                                    {numberFormat(
                                                                        row.team_and_spon_sell
                                                                    )}

                                                                </strong>

                                                            </div>


                                                            {/* MEMBERS */}

                                                            <div className="smart-mobile-item">

                                                                <span>
                                                                    Members
                                                                </span>

                                                                <strong>

                                                                    {numberFormat(
                                                                        row.count_active_spons
                                                                    )}

                                                                </strong>

                                                            </div>

                                                        </div>


                                                        {/* JOIN DATE */}

                                                        <div className="smart-mobile-join">

                                                            <i
                                                                className="fa fa-calendar"
                                                                aria-hidden="true"
                                                            ></i>

                                                            Joined{" "}

                                                            {formatDate(
                                                                row.created_at
                                                            )}

                                                        </div>

                                                    </div>

                                                );
                                            }
                                        )}

                                    </div>

                                </div>

                            </Fragment>
                        )}


                        {/* =====================================
                            PAGINATION
                        ===================================== */}

                        {!loading &&
                            !pageError &&
                            filteredIncome.length > 0 && (

                            <div className="smart-team-footer">


                                {/* INFO */}

                                <div className="smart-team-info">

                                    Showing{" "}

                                    <strong>
                                        {showingFrom}
                                    </strong>

                                    {" "}to{" "}

                                    <strong>
                                        {showingTo}
                                    </strong>

                                    {" "}of{" "}

                                    <strong>
                                        {filteredIncome.length}
                                    </strong>

                                    {" "}members

                                </div>


                                {/* BUTTONS */}

                                <div className="smart-pagination">


                                    {/* FIRST */}

                                    <button
                                        type="button"
                                        className="smart-page-btn"
                                        disabled={
                                            currentPage === 1
                                        }
                                        onClick={() =>
                                            changePage(1)
                                        }
                                        title="First Page"
                                    >

                                        <i
                                            className="fa fa-angle-double-left"
                                            aria-hidden="true"
                                        ></i>

                                    </button>


                                    {/* PREVIOUS */}

                                    <button
                                        type="button"
                                        className="smart-page-btn"
                                        disabled={
                                            currentPage === 1
                                        }
                                        onClick={() =>
                                            changePage(
                                                currentPage - 1
                                            )
                                        }
                                        title="Previous Page"
                                    >

                                        <i
                                            className="fa fa-angle-left"
                                            aria-hidden="true"
                                        ></i>

                                    </button>


                                    {/* PAGE NUMBER */}

                                    {pageNumbers.map(
                                        page => (

                                            <button
                                                type="button"
                                                key={page}
                                                className={
                                                    `smart-page-btn ${
                                                        currentPage === page
                                                            ? "active"
                                                            : ""
                                                    }`
                                                }
                                                onClick={() =>
                                                    changePage(
                                                        page
                                                    )
                                                }
                                            >

                                                {page}

                                            </button>

                                        )
                                    )}


                                    {/* NEXT */}

                                    <button
                                        type="button"
                                        className="smart-page-btn"
                                        disabled={
                                            currentPage ===
                                            totalPages
                                        }
                                        onClick={() =>
                                            changePage(
                                                currentPage + 1
                                            )
                                        }
                                        title="Next Page"
                                    >

                                        <i
                                            className="fa fa-angle-right"
                                            aria-hidden="true"
                                        ></i>

                                    </button>


                                    {/* LAST */}

                                    <button
                                        type="button"
                                        className="smart-page-btn"
                                        disabled={
                                            currentPage ===
                                            totalPages
                                        }
                                        onClick={() =>
                                            changePage(
                                                totalPages
                                            )
                                        }
                                        title="Last Page"
                                    >

                                        <i
                                            className="fa fa-angle-double-right"
                                            aria-hidden="true"
                                        ></i>

                                    </button>

                                </div>

                            </div>

                        )}

                    </section>

                </div>

            </main>

        </Fragment>
    );
};


export default BasicDatatable;